import { Suspense, useState, useContext, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, Bell, LogOut, Home, Stethoscope, Calendar, Settings, Clock, Users, Receipt, ClipboardList, ListChecks, RotateCcw, User, BarChart3, LogIn, Users2, IdCard } from "lucide-react";
import { ClinicAuthContext } from "../auth/ClinicAuthContext";
import api, { gfApi, MEDIA_BASE_URL } from "../api/client";
import useIsMobile from "./useIsMobile";
import { RouteFallback } from "../components/RouteLoading";
import useStackedTableLabels from "./useStackedTableLabels";
import { useToast } from "../components/Feedback";
import "./mobileTables.css";

// requiredPermission — the codename that must be present in this
// employee's permissions (see hasPerm below) for the nav item to show at
// all. Omitted = always visible to any staff member (Home, Settings).
// Doctors requires manage_doctors — the list API itself is open to all
// staff (no separate "view doctors" codename, see views_admin.doctors'
// docstring), but the page is only useful to someone who can manage. "reports" additionally still checks
// canViewReports directly below, now itself backed by view_clinic_reports
// rather than raw is_admin — see clinic_settings' GET response.
export const STAFF_NAV_ITEMS = [
  { key: "dashboard",    label: "Home",         icon: Home,            path: "/admin",              end: true },
  { key: "doctors",      label: "Doctors",      icon: Stethoscope,     path: "/admin/doctors",      requiredPermission: "manage_doctors" },
  { key: "queue",        label: "Queue",        icon: ListChecks,      path: "/admin/queue",        requiredPermission: "view_appointments" },
  { key: "sessions",     label: "Sessions",     icon: Clock,           path: "/admin/sessions",     requiredPermission: "view_appointments" },
  { key: "appointments", label: "Appointments", icon: Calendar,        path: "/admin/appointments", requiredPermission: "view_appointments" },
  { key: "patients",     label: "Patients",     icon: Users,           path: "/admin/patients",     requiredPermission: "view_patients" },
  { key: "invoices",     label: "Invoices",     icon: Receipt,         path: "/admin/invoices",     requiredPermission: "view_clinic_invoices" },
  { key: "refunds",      label: "Refunds",      icon: RotateCcw,       path: "/admin/refunds",      requiredPermission: "view_clinic_invoices" },
  { key: "reports",      label: "Reports",      icon: BarChart3,       path: "/admin/reports", adminOnly: true },
  { key: "treatment-catalog", label: "Treatment Catalog", icon: ClipboardList, path: "/admin/treatment-catalog", requiredPermission: "view_treatments" },
  // Day-to-day HR operations (attendance corrections, leave approval,
  // salary, payroll) — a dedicated HR person holds manage_clinic_hr
  // without necessarily being the org admin. Self-service leave/
  // attendance/payslips live under Settings > Account instead (every
  // staff member's own data, not an oversight page).
  { key: "hr-admin",     label: "HR Admin",     icon: Users2,          path: "/admin/hr-admin",     requiredPermission: "manage_clinic_hr" },
  // Its own permission, not manage_clinic_hr — independently assignable
  // to anyone the org wants configuring reminders, regardless of HR-ops
  // access.
  { key: "reminders",    label: "Reminders",    icon: Bell,            path: "/admin/reminders",    requiredPermission: "manage_clinic_reminders" },
  // Shared reception terminal for clinics where staff don't clock
  // in/out from their own login — the operator (e.g. a receptionist)
  // enters a staff member's service ID to mark it for them. Its own
  // permission, not manage_clinic_hr, so front desk can hold it without
  // full HR-ops access.
  { key: "attendance-kiosk", label: "Attendance Kiosk", icon: IdCard, path: "/admin/attendance-kiosk", requiredPermission: "operate_clinic_attendance_kiosk" },
  // Every remaining sub-tab (General, SMS, Permissions, HR policy) is
  // org-admin-only — personal/self-service settings live under My
  // Account instead (reached from the avatar menu, not this nav).
  { key: "settings",     label: "Settings",     icon: Settings,        path: "/admin/settings", orgAdminOnly: true },
];

// '*' is what Employee.get_permissions() returns for is_admin (see
// gf/models.py) — bypasses every codename check, same as the backend's
// Employee.has_perm.
export function hasPerm(permissions, codename) {
  return permissions.includes("*") || permissions.includes(codename);
}

export const DOCTOR_NAV_ITEMS = [
  { key: "today",        label: "Today",        icon: Home,            path: "/doctor",             end: true },
  { key: "sessions",     label: "Sessions",     icon: Clock,           path: "/doctor/sessions" },
  { key: "appointments", label: "Appointments", icon: Calendar,        path: "/doctor/appointments" },
  { key: "patients",     label: "Patients",     icon: Users,           path: "/doctor/patients" },
  { key: "reports",      label: "Reports",      icon: BarChart3,       path: "/doctor/reports" },
];

const COLORS = {
  brandDark: "#0d4f7a",
  brandMid: "#1a6fa8",
  brandTeal: "#0d9488",
  text: "#0f2944",
  textMuted: "#4a6080",
  textFaint: "#8fa3bc",
  border: "#e2eaf3",
  bg: "#f8fafc",
};

const S = {
  shell: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'DM Sans', sans-serif",
    background: COLORS.bg,
  },
  topbar: {
    height: 60,
    boxSizing: "border-box",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "0 18px",
    background: "white",
    borderBottom: `1px solid ${COLORS.border}`,
    position: "sticky",
    top: 0,
    zIndex: 20,
  },
  iconBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 8,
    borderRadius: 8,
    color: COLORS.textMuted,
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  logoBox: (hasCustomLogo) => ({
    width: 32,
    height: 32,
    borderRadius: 9,
    background: hasCustomLogo ? "white" : `linear-gradient(135deg, ${COLORS.brandMid}, ${COLORS.brandTeal})`,
    border: hasCustomLogo ? `1px solid ${COLORS.border}` : "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
  }),
  logoImg: { width: "100%", height: "100%", objectFit: "contain" },
  brandText: {
    fontFamily: "'Lora', Georgia, serif",
    fontSize: 16,
    fontWeight: 600,
    color: COLORS.brandDark,
  },
  spacer: { flex: 1 },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${COLORS.brandMid}, ${COLORS.brandTeal})`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    overflow: "hidden",
    padding: 0,
  },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  userMenu: {
    position: "absolute",
    top: 52,
    right: 18,
    background: "white",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(13,79,122,0.12)",
    padding: 6,
    minWidth: 150,
    zIndex: 30,
  },
  userMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 12px",
    fontSize: 13.5,
    color: COLORS.text,
    cursor: "pointer",
    borderRadius: 7,
    background: "none",
    border: "none",
    width: "100%",
    textAlign: "left",
  },
  userMenuHeader: {
    padding: "8px 12px 10px",
    borderBottom: `1px solid ${COLORS.border}`,
    marginBottom: 6,
  },
  userMenuName: { fontSize: 13.5, fontWeight: 700, color: COLORS.text, margin: 0 },
  userMenuMeta: { fontSize: 11.5, color: COLORS.textFaint, margin: "2px 0 0" },
  body: {
    flex: 1,
    display: "flex",
    minHeight: 0,
    // flex-start, not the default stretch — otherwise the sidebar's box
    // height gets stretched to match `main`'s content height (which can
    // be tall — e.g. a dashboard full of cards), and the footer's
    // marginTop:auto then pushes it down to align with the bottom of
    // THAT stretched box instead of the bottom of the short nav list.
    alignItems: "flex-start",
  },
  sidebar: (mobile, open) => ({
    width: 220,
    boxSizing: "border-box",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    background: "white",
    borderRight: `1px solid ${COLORS.border}`,
    padding: "18px 12px",
    ...(mobile
      ? {
          position: "fixed",
          top: 60,
          bottom: 0,
          left: 0,
          zIndex: 25,
          overflowY: "auto",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.2s ease",
          boxShadow: open ? "4px 0 24px rgba(13,79,122,0.15)" : "none",
        }
      : {
          // main (not the document) is the scroll container now — see
          // S.main — so the sidebar never needs to move at all. A hard
          // height here (not minHeight) plus its own overflowY keeps the
          // nav list + footer contained to exactly the viewport instead
          // of ever pushing the real page taller.
          height: "calc(100dvh - 60px)",
          overflowY: "auto",
        }),
  }),
  backdrop: {
    position: "fixed",
    inset: 0,
    top: 60,
    background: "rgba(13,41,68,0.25)",
    zIndex: 24,
  },
  navLinkBase: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "10px 14px",
    borderRadius: 9,
    fontSize: 14,
    fontWeight: 500,
    color: COLORS.textMuted,
    textDecoration: "none",
    marginBottom: 3,
  },
  main: {
    flex: 1,
    minWidth: 0,
    // main is its own scroll container (not the document/body) — this is
    // what actually freezes the topbar and sidebar: they simply aren't
    // inside the thing that scrolls, rather than relying on sticky
    // positioning to fight page-level scroll on every page.
    height: "calc(100dvh - 60px)",
    boxSizing: "border-box",
    overflowY: "auto",
    padding: "28px 24px",
  },
  sidebarFooter: {
    marginTop: "auto",
    paddingTop: 16,
    borderTop: `1px solid ${COLORS.border}`,
    textAlign: "center",
    fontSize: 11,
    lineHeight: 1.5,
    color: COLORS.textFaint,
  },
  notifBadge: {
    position: "absolute",
    top: 4,
    right: 4,
    minWidth: 15,
    height: 15,
    padding: "0 3px",
    borderRadius: 999,
    background: "#dc2626",
    color: "white",
    fontSize: 9.5,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  notifPanel: {
    position: "absolute",
    top: 52,
    right: 60,
    width: 340,
    maxHeight: 420,
    overflowY: "auto",
    background: "white",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(13,79,122,0.12)",
    zIndex: 30,
  },
  notifHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "12px 14px",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  notifHeader: {
    fontSize: 12.5,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    margin: 0,
  },
  markAllBtn: {
    border: "none",
    background: "none",
    color: COLORS.brandMid,
    fontSize: 11.5,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
    textTransform: "none",
    letterSpacing: 0,
  },
  notifItem: (isRead) => ({
    padding: "11px 14px",
    borderBottom: `1px solid ${COLORS.border}`,
    cursor: "pointer",
    background: isRead ? "white" : "#eaf4fb",
  }),
  notifTitle: { fontSize: 13, fontWeight: 600, color: COLORS.text, margin: "0 0 3px" },
  notifMessage: { fontSize: 12.5, color: COLORS.textMuted, margin: "0 0 4px", lineHeight: 1.4 },
  notifTime: { fontSize: 11, color: COLORS.textFaint, margin: 0 },
  notifEmpty: { padding: "20px 14px", fontSize: 13, color: COLORS.textFaint, textAlign: "center" },
  clockBtn: (clockedIn) => ({
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: `1px solid ${clockedIn ? "#0d9488" : COLORS.border}`,
    background: clockedIn ? "#e8f7f5" : "white",
    color: clockedIn ? "#0d9488" : COLORS.textMuted,
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
  }),
};

// Self-service clock in/out — every clinic staff member (not doctors,
// who have no Employee row and are out of HR scope for now — see
// AppLayout's showSwitchOrg check where this is rendered). Lives in the
// topbar rather than a settings page since it's used many times a day.
function ClockWidget() {
  const toast = useToast();
  const [today, setToday] = useState(null); // null = loading, undefined = no record yet
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("attendance/clock/").then((res) => setToday(res.data || undefined)).catch(() => setToday(undefined));
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = () => {
    if (busy) return;
    setBusy(true);
    api
      .post("attendance/clock/")
      .then((res) => setToday(res.data))
      .catch((err) => toast.apiError(err, "Could not record attendance."))
      .finally(() => setBusy(false));
  };

  if (today === null) return null;

  const clockedIn = today && today.clock_in_at && !today.clock_out_at;
  const doneForToday = today && today.clock_in_at && today.clock_out_at;
  const label = doneForToday
    ? `Clocked out ${new Date(today.clock_out_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : clockedIn
    ? `Clock out (in since ${new Date(today.clock_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${
        today.required_out_at && (today.late_minutes > 0 || today.status === "LEAVE")
          ? ` · stay until ${new Date(today.required_out_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
          : ""
      })`
    : "Clock in";

  return (
    <button style={S.clockBtn(clockedIn)} onClick={toggle} disabled={busy || doneForToday} title="Record today's attendance">
      {clockedIn ? <Clock size={14} /> : <LogIn size={14} />}
      {label}
    </button>
  );
}

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.end}
      onClick={onClick}
      style={({ isActive }) => ({
        ...S.navLinkBase,
        background: isActive ? "#eaf4fb" : "transparent",
        color: isActive ? COLORS.brandMid : COLORS.textMuted,
        fontWeight: isActive ? 600 : 500,
      })}
    >
      <Icon size={17} />
      {item.label}
    </NavLink>
  );
}

export default function AppLayout({ navItems = STAFF_NAV_ITEMS, fetchBranding = true, brandFallback = null, showSwitchOrg = false }) {
  const isMobile = useIsMobile();
  useStackedTableLabels();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { logout, orgName, orgId, clearOrg, selectOrg } = useContext(ClinicAuthContext);
  const navigate = useNavigate();

  // "Who am I" for the user-menu header — works for both a Doctor and
  // clinic staff (see clinic/views_account.my_account), fetched once
  // regardless of which mount this is.
  const [account, setAccount] = useState(null);
  useEffect(() => {
    api.get("my-account/").then((res) => setAccount(res.data)).catch(() => {});
  }, []);
  // Read by the notification socket handler below, which is created once per
  // org connection and would otherwise close over a stale null account.
  const accountIdRef = useRef(null);
  useEffect(() => { accountIdRef.current = account?.id ?? null; }, [account]);

  const userMenuRef = useRef(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [userMenuOpen]);

  // Clinic-configured branding (see AdminSettings.js "Clinic branding")
  // — falls back to the default logo mark + "Clinic Portal" text below
  // whenever a clinic hasn't set its own. admin/settings/ is staff-only
  // (see views_admin.clinic_settings' _require_clinic_staff), so the
  // doctor-facing shell skips this fetch and falls back to brandFallback
  // (the doctor's currently-selected org name) instead.
  const [branding, setBranding] = useState({ name: null, logoUrl: null });
  // Piggybacks off the same staff-only admin/settings/ fetch above —
  // avoids a second round trip just to learn the org id.
  const [staffOrgId, setStaffOrgId] = useState(null);
  // The flag every financial dashboard tile / the Reports nav item /
  // ReportsPage.js itself checks — backed by the view_clinic_reports
  // permission (see clinic_settings' GET response and hasPerm below),
  // not raw is_admin anymore now that Reports enforcement is real RBAC
  // (views_admin.clinic_reports etc. check view_clinic_reports/
  // download_clinic_reports). is_admin still passes, since
  // Employee.has_perm bypasses for it. Doctors never see any of this
  // (fetchBranding is false on the doctor mount, so this stays false),
  // and there's no "admin doctor" concept today.
  const [canViewReports, setCanViewReports] = useState(false);
  // Settings > General's edit form — separate from canViewReports
  // since it's backed by the manage_clinic_settings permission, not
  // view_clinic_reports (see clinic_settings' docstring in views_admin.py).
  const [canManageSettings, setCanManageSettings] = useState(false);
  // The REAL Employee.is_admin flag — distinct from canViewReports
  // above, which is only the view_clinic_reports permission (is_admin
  // bypasses it too, which is what made the two easy to conflate before
  // this was split out). Org-policy config (leave types, EPF/ETF rates,
  // roster templates) stays gated on this, not on manage_clinic_hr.
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  // Day-to-day HR operations permission — see clinic/views_hr.py's
  // _require_hr_ops. Gates the "HR Admin" nav item/page.
  const [canManageHR, setCanManageHR] = useState(false);
  // Full permission codename list for this employee (or ["*"] for
  // is_admin) — see clinic_settings' GET response and the hasPerm
  // helper above. Drives nav-item visibility and per-action button
  // gating on individual pages (AdminDoctors, AdminAppointments, etc.).
  const [permissions, setPermissions] = useState([]);
  // Some clinics don't let staff self-report their own attendance at all
  // — see HRStatutoryConfig.kiosk_only_attendance. When true, the header
  // ClockWidget below is hidden entirely; only the Attendance Kiosk page
  // (operate_clinic_attendance_kiosk) can mark attendance for that org.
  const [attendanceKioskOnly, setAttendanceKioskOnly] = useState(false);
  // Whether staff can submit attendance-correction requests at all — see
  // HRStatutoryConfig.attendance_corrections_enabled. Defaults true so an
  // org that's never touched this setting keeps today's behavior.
  const [attendanceCorrectionsEnabled, setAttendanceCorrectionsEnabled] = useState(true);

  useEffect(() => {
    if (!fetchBranding) return;
    api
      .get("admin/settings/")
      .then((res) => {
        setBranding({ name: res.data.clinic_name || null, logoUrl: res.data.logo_url || null });
        setStaffOrgId(res.data.organization_id || null);
        // ClinicAuthContext's orgId (clinic_selected_org_id in
        // localStorage) otherwise only ever gets set by the DOCTOR
        // multi-clinic picker (ClinicSelect.js) — a plain staff login
        // never went through that flow, so it silently stayed null
        // forever. That's fine for /clinic/ endpoints (RLS resolves the
        // org from the path itself, see org_type_for_path), but it left
        // the X-Organization-Id header never sent on shared /gf/ calls
        // (org logo upload, SMS test, org settings via gfApi) — for a
        // dual-role user (staff at both a pharmacy AND a clinic org,
        // same login, real case: Golden Life Pharmacy) those calls fell
        // back to resolving the PHARMACY side instead, e.g. a logo
        // uploaded from clinic Settings silently landing on the wrong
        // org's branding. Filling it in here, from the org id this same
        // fetch already resolves correctly, closes that gap for every
        // shared call made from within the clinic app, for every staff
        // login (not just dual-role ones) and self-heals existing
        // sessions on next load, no re-login required.
        if (res.data.organization_id) selectOrg(res.data.organization_id, res.data.clinic_name || "");
        const perms = res.data.permissions || [];
        setPermissions(perms);
        setCanViewReports(hasPerm(perms, "view_clinic_reports"));
        setCanManageSettings(Boolean(res.data.can_manage_settings));
        setIsOrgAdmin(Boolean(res.data.is_admin));
        setCanManageHR(hasPerm(perms, "manage_clinic_hr"));
        setAttendanceKioskOnly(Boolean(res.data.attendance_kiosk_only));
        setAttendanceCorrectionsEnabled(res.data.attendance_corrections_enabled !== false);
      })
      .catch(() => {});
  }, [fetchBranding]);

  // Doctor's own avatar photo — showSwitchOrg is already the doctor-only
  // gate used elsewhere in this file (only passed true on the /doctor
  // AppLayout mount), so it doubles as "am I a doctor" here too rather
  // than adding a new prop. Falls back to the plain "CS" avatar below
  // when no photo is set (see S.avatar's JSX) — never a broken image.
  const [doctorPhotoUrl, setDoctorPhotoUrl] = useState(null);

  useEffect(() => {
    if (!showSwitchOrg) return;
    api
      .get("profile/")
      .then((res) => setDoctorPhotoUrl(res.data.photo_url || null))
      .catch(() => {});
  }, [showSwitchOrg]);

  // Notifications — enabled for both roles. Staff go through gf's
  // Employee-scoped endpoints (gfApi, RLS via employee.organization_id);
  // a doctor JWT has no Employee record, so it goes through clinic's own
  // doctor-facing endpoints instead (see clinic.views_clinic.
  // doctor_notifications/doctor_mark_notification_read, and
  // clinic.notifications.notify_doctor for what creates these rows —
  // e.g. clinic staff cancelling a session on the doctor's behalf).
  // showSwitchOrg is already the doctor-only gate used elsewhere in this
  // file, so it doubles as "use the clinic API, not gf" here too.
  const notifEnabled = fetchBranding || showSwitchOrg;
  const notifClient = showSwitchOrg ? api : gfApi;
  // Staff: org id comes from the branding fetch above. Doctor: from
  // whichever clinic they currently have selected (ClinicAuthContext) —
  // notifications from a doctor's OTHER clinics still show up via the
  // REST list below, they just won't arrive live over this socket
  // unless that other clinic happens to be the one currently selected.
  const wsOrgId = notifEnabled ? (staffOrgId || orgId) : null;
  const wsTarget = showSwitchOrg ? "doctor" : "clinic_staff";
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    if (!notifEnabled) return;
    notifClient
      .get("notifications/")
      .then((res) => setNotifications(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifEnabled, showSwitchOrg]);

  useEffect(() => {
    if (!wsOrgId) return;

    const baseUrl = (process.env.REACT_APP_GF_BASE_URL || "http://localhost:8000/gf/").replace(/\/gf\/?$/, "");
    const wsUrl = baseUrl.replace("https://", "wss://").replace("http://", "ws://");

    let socket;
    let reconnectTimer;
    let destroyed = false;
    // Reconnect delay doubles after every failed attempt (3s, 6s, 12s ... up to
    // 60s) with a little jitter, and resets once a connection succeeds. A fixed
    // 3s retry hammers the server and drains a phone battery when the network
    // is flaky or the server is down; and it makes every client reconnect in
    // lockstep after a restart.
    let attempt = 0;
    const nextDelay = () => Math.min(3000 * 2 ** attempt, 60000) * (1 + Math.random() * 0.2);

    const connect = () => {
      if (destroyed) return;
      socket = new WebSocket(`${wsUrl}/ws/org/${wsOrgId}/`);
      // Deliberately logged (not silent) — same as pharmacy-frontend's
      // Layout.js — so "notifications only show after a refresh" is
      // diagnosable from the browser console: an open/close pair with
      // no drops means the connection is fine and the gap is elsewhere;
      // repeated failures here point at the network path (proxy, VPN,
      // firewall) not carrying the WS upgrade through, even though
      // plain HTTP to the same host works.
      socket.onopen = () => {
        attempt = 0;
        console.log("Notifications WebSocket connected");
      };
      socket.onerror = (e) => console.error("Notifications WebSocket error", e);
      socket.onmessage = (event) => {
        if (destroyed) return;
        const data = JSON.parse(event.data);
        if (data.type !== "notification" || data.target !== wsTarget) return;
        // The socket group is the whole org, so a push for one person (e.g. "your
        // update was approved") reaches everyone connected — only show it to
        // the users it was actually addressed to.
        if (Array.isArray(data.recipient_user_ids) && accountIdRef.current != null
            && !data.recipient_user_ids.includes(accountIdRef.current)) return;
        setNotifications((prev) => [
          {
            // notification_id (the Notification row's own PK) is what
            // mark-as-read needs — NOT UserNotification's PK, which is
            // what the REST list's plain "id" field is (see
            // gf.serializers.UserNotificationSerializer). Naming this
            // notification_id here too, never id, keeps both sources
            // consistent so handleNotificationClick can't reach for the
            // wrong one.
            notification_id: data.notification_id,
            title: data.title,
            message: data.message,
            entity_route: data.entity_route,
            created_at: data.created_at,
            is_read: false,
          },
          ...prev,
        ]);
      };
      socket.onclose = () => {
        if (destroyed) return;
        // A hidden tab (phone locked, another app in front) isn't worth
        // retrying for; it reconnects as soon as the tab is visible again.
        if (document.hidden) {
          console.log("Notifications WebSocket disconnected — will reconnect when the tab is visible");
          return;
        }
        const delay = nextDelay();
        attempt += 1;
        console.log(`Notifications WebSocket disconnected — retrying in ${Math.round(delay / 1000)}s`);
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    const resume = () => {
      if (destroyed || document.hidden) return;
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
      clearTimeout(reconnectTimer);
      attempt = 0;
      connect();
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("online", resume);

    connect();
    return () => {
      destroyed = true;
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("online", resume);
      clearTimeout(reconnectTimer);
      if (socket && socket.readyState !== WebSocket.CLOSED) socket.close();
    };
  }, [wsOrgId]);

  const notifPanelRef = useRef(null);
  useEffect(() => {
    if (!notifOpen) return;
    const onClickOutside = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [notifOpen]);

  const markAllRead = () => {
    notifClient.post("notifications/mark-all-read/").catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const handleNotificationClick = (n) => {
    if (!n.is_read) {
      // mark_notification_as_read looks up UserNotification by
      // notification_id (the Notification's own PK) — n.id from the
      // REST list is UserNotification's PK instead, a different number;
      // using it here 404s with "Notification not found for user."
      notifClient.post(`notifications/${n.notification_id}/mark-as-read/`).catch(() => {});
      setNotifications((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, is_read: true } : x)));
    }
    setNotifOpen(false);
    // Guard against a bad/foreign entity_route (e.g. one meant for a
    // different frontend — this bit once: a clinic notification carried
    // a pharmacy-frontend route, which fell through App.js's catch-all
    // `<Route path="*" element={<Navigate to="/login" replace />} />`
    // and looked exactly like an unexplained logout on click). Only
    // navigate within this app's own known route prefixes; a route
    // outside them just closes the panel instead of risking that trap.
    if (n.entity_route && /^\/(admin|doctor|select-clinic)(\/|$)/.test(n.entity_route)) {
      navigate(n.entity_route);
    }
  };

  const brandName = branding.name || brandFallback || orgName;

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div style={S.shell}>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Lora:wght@600&display=swap"
        rel="stylesheet"
      />
      {/* This app has no global stylesheet — everything is inline styles.
          The browser's default 8px body margin was invisible before (it
          just padded the page), but now that the topbar/sidebar/main
          heights are all pinned to exactly 100dvh, that stray 8px was
          enough to push the layout past the viewport and force a
          spurious page-level scrollbar. */}
      <style>{"body { margin: 0; }"}</style>

      <div style={S.topbar}>
        {isMobile && (
          <button style={S.iconBtn} onClick={() => setDrawerOpen((o) => !o)} aria-label="Toggle menu">
            {drawerOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        )}
        <div style={S.brandRow}>
          <div style={S.logoBox(Boolean(branding.logoUrl))}>
            {branding.logoUrl ? (
              <img src={`${MEDIA_BASE_URL}${branding.logoUrl}`} alt={branding.name || "Clinic logo"} style={S.logoImg} />
            ) : (
              <svg viewBox="0 0 100 100" width={18} height={18} fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M30 20 V45 C30 58 40 65 50 65 C60 65 70 58 70 45 V20" />
                <line x1="30" y1="20" x2="30" y2="14" />
                <line x1="70" y1="20" x2="70" y2="14" />
                <line x1="50" y1="65" x2="50" y2="78" />
                <circle cx="50" cy="86" r="7" fill="white" stroke="none" />
              </svg>
            )}
          </div>
          {!isMobile && <span style={S.brandText}>{brandName || "Clinic Portal"}</span>}
        </div>

        <div style={S.spacer} />

        {!showSwitchOrg && !attendanceKioskOnly && <ClockWidget />}

        {notifEnabled ? (
          <div style={{ position: "relative" }} ref={notifPanelRef}>
            <button
              style={{ ...S.iconBtn, position: "relative" }}
              aria-label="Notifications"
              title="Notifications"
              onClick={() => setNotifOpen((o) => !o)}
            >
              <Bell size={19} />
              {unreadCount > 0 && <span style={S.notifBadge}>{unreadCount > 9 ? "9+" : unreadCount}</span>}
            </button>
            {notifOpen && (
              <div style={S.notifPanel}>
                <div style={S.notifHeaderRow}>
                  <p style={S.notifHeader}>Notifications</p>
                  {unreadCount > 0 && (
                    <button style={S.markAllBtn} onClick={markAllRead}>Mark all read</button>
                  )}
                </div>
                {notifications.length === 0 && <p style={S.notifEmpty}>No notifications yet.</p>}
                {notifications.map((n) => (
                  <div key={n.notification_id} style={S.notifItem(n.is_read)} onClick={() => handleNotificationClick(n)}>
                    <p style={S.notifTitle}>{n.title}</p>
                    <p style={S.notifMessage}>{n.message}</p>
                    <p style={S.notifTime}>{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button style={S.iconBtn} aria-label="Notifications" title="Notifications (coming soon)">
            <Bell size={19} />
          </button>
        )}

        <div style={{ position: "relative" }} ref={userMenuRef}>
          <button style={S.avatar} onClick={() => setUserMenuOpen((o) => !o)} aria-label="User menu">
            {doctorPhotoUrl ? (
              <img src={`${MEDIA_BASE_URL}${doctorPhotoUrl}`} alt="" style={S.avatarImg} />
            ) : (
              <span>CS</span>
            )}
          </button>
          {userMenuOpen && (
            <div style={S.userMenu}>
              {account && (
                <div style={S.userMenuHeader}>
                  <p style={S.userMenuName}>{account.full_name}</p>
                  <p style={S.userMenuMeta}>{account.email || account.username}{account.service_id ? ` · ${account.service_id}` : ""}</p>
                </div>
              )}
              {showSwitchOrg ? (
                <>
                  <button
                    style={S.userMenuItem}
                    onClick={() => { setUserMenuOpen(false); navigate("/doctor/profile"); }}
                  >
                    <User size={15} />
                    My Profile
                  </button>
                  <button
                    style={S.userMenuItem}
                    onClick={() => { clearOrg(); navigate("/select-clinic"); }}
                  >
                    <Stethoscope size={15} />
                    Switch clinic
                  </button>
                </>
              ) : (
                <button
                  style={S.userMenuItem}
                  onClick={() => { setUserMenuOpen(false); navigate("/admin/my-account"); }}
                >
                  <User size={15} />
                  My Account
                </button>
              )}
              <button
                style={S.userMenuItem}
                onClick={() => { logout(); navigate("/login"); }}
              >
                <LogOut size={15} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={S.body}>
        {isMobile && drawerOpen && <div style={S.backdrop} onClick={closeDrawer} />}

        <div style={S.sidebar(isMobile, drawerOpen)}>
          {navItems
            .filter((item) => !item.adminOnly || canViewReports)
            .filter((item) => !item.orgAdminOnly || isOrgAdmin)
            .filter((item) => !item.requiredPermission || hasPerm(permissions, item.requiredPermission))
            .map((item) => (
              <NavItem key={item.key} item={item} onClick={isMobile ? closeDrawer : undefined} />
            ))}
          <div style={S.sidebarFooter}>
            Clinic BSS
            <br />
            © {new Date().getFullYear()} Coffer Smart
          </div>
        </div>

        <div style={S.main}>
          {/* Own boundary so navigating to a not-yet-downloaded page keeps the sidebar and top bar on screen. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet context={{ canViewReports, canManageSettings, permissions, isOrgAdmin, canManageHR, attendanceCorrectionsEnabled }} />
          </Suspense>
        </div>
      </div>
    </div>
  );
} 